//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  getBubbleStats,
  getEmailSignupCount,
  getGraphStats,
  getLabels,
} from '@apis/metrics/metricsApi';
import {
  EcommerceSessionType,
  IStatsRequest,
  InsightsSourceType,
} from '@apis/metrics/metricsApiModel';
import { initialBaseStatsRequest } from '@insights/insightsConstants';
import {
  EMPTY_UNIFIED_METRICS_BUBBLE_STATS_RESPONSE,
  IInsightsActivityStats,
  IInsightsBubbleStats,
  IInsightsGraphStats,
  IInsightsPage,
} from '@insights/redux/insightsPageModel';
import {
  fetchApiActivityStats,
  getTestVariationFromParams,
} from '@insights/redux/insightsPageUtils';
import { getMetadata } from '@insights/redux/metadata/insightsMetadataSlice';
import { getLike2BuyV2Insights } from '@insights/redux/selectors/getLike2BuyV2Insights';
import { ILabel } from '@models/ILabel';
import Interval from '@models/Interval';
import { SocialNetwork } from '@models/SocialNetwork';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import IntervalHelper from '@utils/intervalHelper';

const initialState: IInsightsPage = {
  statsRequestBase: undefined,
  bubbleStats: undefined,
  graphStats: undefined,
  activityStats: initialBaseStatsRequest,
  allLabels: undefined,
  flags: {
    isFetchingBubbleStats: false,
    isFetchingGraphStats: false,
    isFetchingActivityStats: false,
    isFetchingLabels: false,
  },
  errors: {
    fetchBubbleStatsError: undefined,
    fetchGraphStatsError: undefined,
    fetchActivityStatsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.InsightsLike2BuyV2,
  initialState,
  reducers: {
    requestBubbleStats(state) {
      state.flags.isFetchingBubbleStats = true;
      state.errors.fetchBubbleStatsError = undefined;
    },
    recieveBubbleStatsSucceeded(
      state,
      action: PayloadAction<IInsightsBubbleStats>,
    ) {
      state.flags.isFetchingBubbleStats = false;
      state.bubbleStats = action.payload;
    },
    recieveBubbleStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingBubbleStats = false;
      state.errors.fetchBubbleStatsError = action.payload;
    },
    requestGraphStats(state) {
      state.flags.isFetchingGraphStats = true;
      state.errors.fetchGraphStatsError = undefined;
    },
    recieveGraphStatsSucceeded(
      state,
      action: PayloadAction<IInsightsGraphStats>,
    ) {
      state.flags.isFetchingGraphStats = false;
      state.graphStats = action.payload;
    },
    recieveGraphStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingGraphStats = false;
      state.errors.fetchGraphStatsError = action.payload;
    },
    requestActivityStats(state) {
      state.flags.isFetchingActivityStats = true;
      state.errors.fetchActivityStatsError = undefined;
    },
    recieveActivityStatsSucceeded(
      state,
      action: PayloadAction<IInsightsActivityStats>,
    ) {
      state.flags.isFetchingActivityStats = false;
      state.activityStats = action.payload;
    },
    recieveActivityStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingActivityStats = false;
      state.errors.fetchActivityStatsError = action.payload;
    },
    updateActivityStats(
      state,
      action: PayloadAction<Partial<IInsightsActivityStats>>,
    ) {
      state.activityStats = {
        ...state.activityStats,
        ...action.payload,
      };
    },
    requestLabels(state) {
      state.flags.isFetchingLabels = true;
      state.errors.fetchLabelsError = undefined;
    },
    recieveLabelsSucceeded(state, action: PayloadAction<ILabel[]>) {
      state.flags.isFetchingLabels = false;
      state.allLabels = action.payload;
    },
    recieveLabelsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingLabels = false;
      state.errors.fetchLabelsError = action.payload;
    },
    setStatsRequestBase(state, action: PayloadAction<IStatsRequest>) {
      state.statsRequestBase = action.payload;
    },
    resetState(state) {
      return initialState;
    },
  },
});

export const fetchBubbleStats = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getLike2BuyV2Insights(getState());
    const { dateRange } = getState().insights.metadata;

    if (statsRequestBase) {
      const statsRequest = isDefined(statsRequestBase.comparisonInterval)
        ? statsRequestBase
        : {
            ...statsRequestBase,
            comparisonInterval: Interval.fromDateRange(
              IntervalHelper.getComparisonDateRange(dateRange),
            ),
          };

      dispatch(actions.requestBubbleStats());

      try {
        const bubbleStatsRes = await getBubbleStats(statsRequest);
        const emailStatsRes = await getEmailSignupCount(statsRequestBase);
        const bubbleStats: IInsightsBubbleStats = {
          stats:
            bubbleStatsRes[0] ?? EMPTY_UNIFIED_METRICS_BUBBLE_STATS_RESPONSE,
          emailSignups: emailStatsRes,
        };
        dispatch(actions.recieveBubbleStatsSucceeded(bubbleStats));
      } catch (e) {
        dispatch(
          actions.recieveBubbleStatsFailed(
            getRequestErrorMessage(e) ??
              '[fetchBubbleStats] Failed to fetch bubble stats',
          ),
        );
      }
    }
  },
);

export const fetchGraphStats = createThunk(() => async (dispatch, getState) => {
  const { statsRequestBase } = getLike2BuyV2Insights(getState());

  if (statsRequestBase) {
    dispatch(actions.requestGraphStats());

    try {
      const graphStatsRes = await getGraphStats(statsRequestBase);
      const graphStats: IInsightsGraphStats = {
        stats: graphStatsRes,
      };
      dispatch(actions.recieveGraphStatsSucceeded(graphStats));
    } catch (e) {
      dispatch(
        actions.recieveGraphStatsFailed(
          getRequestErrorMessage(e) ??
            '[fetchGraphStats] Failed to fetch graph stats',
        ),
      );
    }
  }
});

export const fetchActivityStats = createThunk(
  () => async (dispatch, getState) => {
    const { activityStats, statsRequestBase } =
      getLike2BuyV2Insights(getState());
    const { contentType, limit, offset, sortDir, sortKey } = activityStats;

    if (statsRequestBase) {
      dispatch(actions.requestActivityStats());

      try {
        const itemProductStatsRequest = {
          ...statsRequestBase,
          sortKey,
          sortDirection: sortDir,
          limit,
          offset,
          preferredNetworks: [SocialNetwork.TikTok], // TODO: PD-220891 - When we migrate Instagram Like2Buy to V2 or support different preferredNetworks we should pass the `preferredNetworks` list that is associated with the store's datasource
        };
        const activityStatsRes: IInsightsActivityStats =
          await fetchApiActivityStats(
            contentType,
            itemProductStatsRequest,
            activityStats,
          );
        dispatch(actions.recieveActivityStatsSucceeded(activityStatsRes));
      } catch (e) {
        dispatch(
          actions.recieveActivityStatsFailed(
            getRequestErrorMessage(e) ??
              '[fetchActivityStats] Failed to fetch activity stats',
          ),
        );
      }
    }
  },
);

export const fetchLabels = createThunk(() => async (dispatch, getState) => {
  const { statsRequestBase } = getLike2BuyV2Insights(getState());

  if (statsRequestBase) {
    dispatch(actions.requestLabels());

    try {
      const labelsResponse = await getLabels(statsRequestBase);
      dispatch(
        actions.recieveLabelsSucceeded(
          labelsResponse.map((label) => {
            return {
              label: label.label,
              count: label.count,
              value: label.label,
            };
          }),
        ),
      );
    } catch (e) {
      dispatch(
        actions.recieveLabelsFailed(
          getRequestErrorMessage(e) ?? '[fetchLabels] Failed to fetch labels',
        ),
      );
    }
  }
});

export const initStatsRequestBase = createThunk(
  (ecommerceSessionType: EcommerceSessionType, storeId?: string) =>
    async (dispatch, getState) => {
      if (!getState().insights.metadata.flags.hasFetchedMetadata) {
        await dispatch(getMetadata());
      }
      const { createdOn, dateRange } = getState().insights.metadata;
      void dispatch(
        getStats({
          interval: IntervalHelper.fromDateRange(dateRange, createdOn),
          ecommerceSessionType: ecommerceSessionType,
          sourceType: [InsightsSourceType.L2B_V2],
          sourceId: storeId,
          testVariation: getTestVariationFromParams(),
        }),
      );
    },
);

export const getStats = createThunk(
  (statsRequestBase: IStatsRequest) => (dispatch) => {
    dispatch(actions.setStatsRequestBase(statsRequestBase));
    void dispatch(fetchBubbleStats());
    void dispatch(fetchGraphStats());
    void dispatch(fetchActivityStats());
    void dispatch(fetchLabels());
  },
);

export const { resetState, updateActivityStats } = actions;

export { reducer };
